'use client'

import { type RefObject, useEffect } from 'react'

import { chooseMove, dwellMs, PAUSE_DWELL, pickStrollTarget } from './roam-behavior'
import {
  GROUND_EPS,
  groundTop,
  type Ledge,
  overlapsX,
  resolveLedge,
  snapshotLedges
} from './roam-geometry'
import type { PetState, Point } from './types'

const STRIDE_PER_LOOP = 0.8
const GRAVITY_PX_S2 = 5200
const JUMP_DUR_MS = 460
const DROP_SETTLE_MS = 90
const ARRIVE_EPS = 1.5
const MAX_DT_S = 0.05

type Phase = 'pause' | 'walk' | 'fall' | 'jump'

const rand = (min: number, max: number): number => min + Math.random() * (max - min)
const easeOutCubic = (t: number): number => 1 - (1 - t) ** 3
const signDir = (n: number): -1 | 0 | 1 => (n > 0 ? 1 : n < 0 ? -1 : 0)

interface UsePetRoamOptions {
  enabled: boolean
  containerRef: RefObject<HTMLDivElement | null>
  isInteracting: () => boolean
  petW: number
  petH: number
  loopMs: number
  onStateChange: (state: PetState, dir: -1 | 0 | 1) => void
  onPositionCommit: (point: Point) => void
}

export function usePetRoam({
  enabled,
  containerRef,
  isInteracting,
  petW,
  petH,
  loopMs,
  onStateChange,
  onPositionCommit
}: UsePetRoamOptions): void {
  useEffect(() => {
    if (!enabled) {
      onStateChange('idle', 0)
      return
    }

    const el = containerRef.current
    if (!el) return

    const walkSpeedPxS = (petW * STRIDE_PER_LOOP) / (loopMs / 1000)
    const restY = (ledge: Ledge): number => groundTop(ledge, petH)

    const rect = el.getBoundingClientRect()
    const cur: Point = { x: rect.left, y: rect.top }

    let phase: Phase = 'pause'
    let pauseUntil = performance.now() + rand(400, 1200)
    let last = performance.now()
    let raf = 0

    let walkTargetX = cur.x
    let curLedge: Ledge | null = null
    let targetLedge: Ledge | null = null
    let pendingHop: Ledge | null = null
    let fallVel = 0
    let jumpFromY = 0
    let jumpElapsed = 0

    const applyDom = () => {
      el.style.left = `${cur.x}px`
      el.style.top = `${cur.y}px`
    }

    const beginPause = (now: number) => {
      phase = 'pause'
      pauseUntil = now + dwellMs(PAUSE_DWELL)
      onStateChange('idle', 0)
      onPositionCommit({ ...cur })
    }

    const settleOn = (ledge: Ledge, now: number) => {
      cur.y = restY(ledge)
      curLedge = ledge
      applyDom()
      beginPause(now)
    }

    const beginVertical = (ledge: Ledge) => {
      targetLedge = ledge
      if (restY(ledge) < cur.y - 1) {
        phase = 'jump'
        jumpFromY = cur.y
        jumpElapsed = 0
      } else {
        phase = 'fall'
        fallVel = 0
      }
      onStateChange('jump', 0)
    }

    const planNext = (now: number) => {
      const ledges = snapshotLedges(petW, petH)
      curLedge = resolveLedge(ledges, cur.x, cur.y, petH)

      if (Math.abs(cur.y - restY(curLedge)) > GROUND_EPS) {
        beginVertical(curLedge)
        return
      }

      const reachable = ledges.filter(ledge => ledge !== curLedge && overlapsX(curLedge!, ledge))
      const move = chooseMove(reachable.length > 0)

      if (move === 'rest') {
        beginPause(now)
        return
      }

      if (move === 'hop') {
        const next = reachable[Math.floor(Math.random() * reachable.length)]!
        const lo = Math.max(curLedge.left, next.left)
        const hi = Math.min(curLedge.right, next.right)
        pendingHop = next
        walkTargetX = lo + Math.random() * (hi - lo)
      } else {
        pendingHop = null
        walkTargetX = pickStrollTarget(curLedge, cur.x)
      }

      phase = 'walk'
      onStateChange('run', signDir(walkTargetX - cur.x))
    }

    const step = (now: number) => {
      const dt = Math.min(MAX_DT_S, (now - last) / 1000)
      last = now

      if (isInteracting()) {
        const live = el.getBoundingClientRect()
        cur.x = live.left
        cur.y = live.top
        phase = 'pause'
        pendingHop = null
        pauseUntil = now + DROP_SETTLE_MS
        onStateChange('idle', 0)
        raf = requestAnimationFrame(step)
        return
      }

      switch (phase) {
        case 'pause': {
          if (now >= pauseUntil) planNext(now)
          break
        }
        case 'walk': {
          const remaining = walkTargetX - cur.x
          const stepDist = walkSpeedPxS * dt
          if (Math.abs(remaining) <= Math.max(ARRIVE_EPS, stepDist)) {
            cur.x = walkTargetX
            applyDom()
            if (pendingHop) {
              const next = pendingHop
              pendingHop = null
              beginVertical(next)
            } else {
              beginPause(now)
            }
          } else {
            cur.x += Math.sign(remaining) * stepDist
            applyDom()
          }
          break
        }
        case 'fall': {
          if (!targetLedge) { beginPause(now); break }
          fallVel += GRAVITY_PX_S2 * dt
          cur.y += fallVel * dt
          if (cur.y >= restY(targetLedge)) {
            settleOn(targetLedge, now)
          } else {
            applyDom()
          }
          break
        }
        case 'jump': {
          if (!targetLedge) { beginPause(now); break }
          jumpElapsed += dt * 1000
          const t = Math.min(1, jumpElapsed / JUMP_DUR_MS)
          cur.y = jumpFromY + (restY(targetLedge) - jumpFromY) * easeOutCubic(t)
          if (t >= 1) {
            settleOn(targetLedge, now)
          } else {
            applyDom()
          }
          break
        }
      }

      raf = requestAnimationFrame(step)
    }

    raf = requestAnimationFrame(step)

    return () => {
      cancelAnimationFrame(raf)
      onStateChange('idle', 0)
      onPositionCommit({ ...cur })
    }
  }, [enabled, petW, petH, loopMs, containerRef, isInteracting, onStateChange, onPositionCommit])
}
